import useCustomerSubscriptionMutations from "features/subscriptions/datahooks/useCustomerSubscriptionMutations";
import useRentalMutations from "features/subscriptions/datahooks/useRentalMutations";
import {
  getCouponUpdateState,
  trackSubscriptionEvent,
} from "features/subscriptions/utils";

import useLicenseMutations from "common/datahooks/useLicenseMutations";
import type {
  AgencySubscription,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

export default function useUpdateSubscription(
  subscriptionType: SubscriptionType,
  subscription?: AgencySubscription | Subscription | RentedSubscription,
) {
  const {
    updateCustomerSubscription,
    isUpdatingCustomerSubscription,
    updateCustomerSubscriptionError,
  } = useCustomerSubscriptionMutations();

  const {
    addRentalAccounts,
    isAddingRentalAccounts,
    addRentalAccountsError,
    updateRentalSubscription,
    isUpdatingRentalSubscription,
    updateRentalSubscriptionError,
  } = useRentalMutations();

  const { setLicenseCount, isSettingLicenseCount, setLicenseCountError } =
    useLicenseMutations();

  async function updateSubscription({
    quantity,
    planId,
    paymentSourceId,
    accountsToCancel,
    paidAmount,
    couponAction,
  }: {
    quantity: number;
    planId: string;
    paymentSourceId: string;
    accountsToCancel: string[];
    paidAmount: number;
    couponAction?: string;
  }) {
    const couponUpdateState = getCouponUpdateState(couponAction);

    let subscriptionId: string;
    if (subscriptionType === "user") {
      const { workspace } = await setLicenseCount({
        count: quantity,
        planId,
        accountsToCancel,
        couponUpdateState,
      });
      subscriptionId = workspace.subscription.id;
    } else if (subscriptionType === "agency") {
      const { subscription } = await updateCustomerSubscription({
        planPricingId: planId,
        count: quantity,
        paymentSourceId,
        couponUpdateState,
      });
      subscriptionId = subscription.id;
    } else if (
      subscription?.type === "rental" &&
      subscription.status !== "cancelled"
    ) {
      //updates an existing rental order (seats and/or billing period)
      const { subscription: updatedSubscription } =
        await updateRentalSubscription({
          subscriptionId: subscription.id,
          currentCount: subscription.rented_count,
          currentItemPriceId: subscription.item_price.id,
          newCount: quantity,
          newItemPriceId: planId,
          accountIds: accountsToCancel,
        });
      subscriptionId = updatedSubscription.id;
    } else {
      //reactivation of a cancelled rental subscription (e.g. from the lock screen)
      const { workspace } = await addRentalAccounts({
        rentalCount: quantity,
        planPriceId: planId,
        couponUpdateState,
      });
      subscriptionId =
        workspace.rental_subscriptions.at(-1)?.id ??
        workspace.rental_subscriptions[0]?.id;
    }
    trackSubscriptionEvent("purchase", subscriptionId, paidAmount, quantity);
  }

  return {
    updateSubscription,
    isUpdatingSubscription:
      isUpdatingCustomerSubscription ||
      isAddingRentalAccounts ||
      isUpdatingRentalSubscription ||
      isSettingLicenseCount,
    updateSubscriptionError:
      updateCustomerSubscriptionError ||
      addRentalAccountsError ||
      updateRentalSubscriptionError ||
      setLicenseCountError,
  };
}
